import { FormEvent, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import classes from './UITable.module.scss';
import Modal from '../modal/Modal';
import { IPageData } from 'common/interfaces';
import UILogger from 'common/utils/logger';
import { Alert, Box, Card, Pagination, Paper, Stack, TextField, Typography } from '@mui/material';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import PasswordIcon from '@mui/icons-material/Password';

export type DisplayItem = {
  label?: string;
  icon?: React.ReactNode;
  isBold?: boolean;
  textAlign?: 'right' | 'center' | 'left';
  flex?: number;
};

export type RowItem = {
  index: number;
  id: number | string;
  data: DisplayItem[];
};

export type RowItemAction<T> = { actionKey: string; handler: (item: T) => void };

interface UITableProps<T> {
  headers?: DisplayItem[];
  title?: string;
  pagedData: IPageData<T> | undefined | null;
  keyExtractor: (item: T, index: number) => string;
  pageSize: number;
  renderRowItem?: (item: T, index: number) => ReactElement;
  extractRowItem?: (item: T, index: number) => RowItem;
  loadData?: (pageNo: number, pageSize: number) => Promise<IPageData<T> | undefined>;
  onDeleteItemConfirmed?: (item: RowItem) => void;
  onEditItemPressed?: (item: RowItem) => void;
  onItemPressed?: (item: T) => void;
  viewOnly?: boolean;
  isLoading?: boolean;
  stickyHeader?: boolean;
  hidePagination?: boolean;
  hideActions?: boolean;
  actionItems?: RowItemAction<T>[];
  noItemFoundMsg?: string;
  titleRightComponent?: ReactElement;
  maxHeight?: number;
  stickyOffsetTop?: number;
}

const UITable = <T,>({
  title,
  headers,
  keyExtractor,
  pagedData,
  pageSize,
  loadData,
  onDeleteItemConfirmed,
  onEditItemPressed,
  onItemPressed,
  extractRowItem,
  renderRowItem,
  viewOnly,
  noItemFoundMsg,
  isLoading,
  titleRightComponent,
  hidePagination,
  hideActions,
  actionItems,
  stickyHeader,
  maxHeight,
  stickyOffsetTop,
}: UITableProps<T>) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RowItem | undefined>();
  const [currentPage, setCurrentPage] = useState(pagedData?.currentPage || 0);
  const [totalPages, setTotalPages] = useState(pagedData?.totalPages || 0);
  const [items, setItems] = useState<T[]>();
  const [isLoadingItems, setLoadingItems] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    console.log('UIList::: currentPage', pagedData?.currentPage);
    setCurrentPage(pagedData?.currentPage || 1);
    setTotalPages(pagedData?.totalPages || 1);
    setItems(pagedData?.items || undefined);
    if (pagedData?.currentPage == 1) {
    }
  }, [pagedData]);

  const fetchMoreData = async (pageIndex: number) => {
    UILogger.log('Fetching more items:', isLoadingItems, pageIndex, pagedData?.totalPages);
    const pageNo = pageIndex + 1;
    if (!isLoadingItems && pagedData && pageNo <= pagedData.totalPages) {
      setLoadingItems(true);
      if (loadData) {
        await loadData(pageNo, pageSize);
        setCurrentPage(pageNo);
        setLoadingItems(false);
      }
    }
  };

  function showModalHandler(item: RowItem) {
    setShowModal(true);
    setSelectedItem(item);
  }

  const _onDeleteItemConfirmed = () => {
    onDeleteItemConfirmed && selectedItem && onDeleteItemConfirmed(selectedItem);
  };
  const _onEditItemPressed = (item: RowItem) => {
    onEditItemPressed && selectedItem && onEditItemPressed(selectedItem);
  };

  const renderRow = (item: T, index: number) => {
    if (renderRowItem) {
      return renderRowItem(item, index);
    } else if (extractRowItem) {
      const items = extractRowItem(item, index);
      return (
        <>
          {items.data.map((item, i) => (
            <td
              style={{ fontWeight: item.isBold ? 'bold' : '300', flex: item.flex || 0 }}
              key={'td:' + index + ':' + i}
            >
              <Box display="flex" flexDirection={{ xs: 'row' }} gap={2}>
                {item.icon}
                <span style={{ width: '100%', textAlign: item.textAlign || 'left' }}>{item.label}</span>
              </Box>
            </td>
          ))}
        </>
      );
    }
    return <></>;
  };

  const _handleRowPressed = (event: FormEvent, item: T) => {
    event.stopPropagation();
    onItemPressed && onItemPressed(item);
  };

  const _handleActionItemPressed = (event: FormEvent, item: T, actionItem: RowItemAction<T>) => {
    event.stopPropagation();
    actionItem && actionItem.handler(item);
  };

  const getActionIcon = (actionKey: string) => {
    switch (actionKey) {
      case 'changePassword':
        return <PasswordIcon />;
    }
  };

  const renderTableRow = (item: T, index: number) => {
    return (
      <tr key={keyExtractor(item, index) || '' + index} onClick={(event) => _handleRowPressed(event, item)}>
        {/* {item.data?.map((data) => (
          <>
            <td className={classes.userName}>
              {data.icon ? <img className={classes.avatar} src={data.icon} alt="icon" /> : null}
              {data.label}
            </td>
          </>
        ))} */}
        {renderRow(item, index)}

        {!viewOnly && !hideActions ? (
          <td className={classes.actions}>
            <Icon icon="charm:menu-kebab" />
            <div className={classes.actions__box}>
              {actionItems?.map((action) => (
                <div
                  className={classes.actions__edit}
                  onClick={(event) => _handleActionItemPressed(event, item, action)}
                >
                  {getActionIcon(action.actionKey)}
                </div>
              ))}
              {/* <div className={classes.actions__delete} onClick={() => showModalHandler}>
                <Icon icon="fluent:delete-24-regular" width="24" />
              </div> */}
              {/* <div className={classes.actions__edit} onClick={() => _onEditItemPressed}>
                <Icon icon="fluent:edit-16-regular" width="24" />
              </div> */}
            </div>
          </td>
        ) : null}
      </tr>
    );
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    fetchMoreData(value - 1);
  };

  return (
    <>
      {/* modal for delete customer and product case*/}
      {showModal ? (
        <Modal title={t('deleteCustomer')} message={`${t('modalMessage')}`} onConfirm={_onDeleteItemConfirmed} />
      ) : null}

      <Card className={classes.cardContainer} sx={{ overflow: 'visible', position: 'relative' }}>
        {title ? (
          <Box
            component={'div'}
            flexDirection={'row'}
            alignItems={'center'}
            bgcolor="info"
            borderRadius="lg"
            boxShadow="info"
            className={classes.tableTitle}
            style={{ marginTop: !title ? '-10px' : '-24px' }}
          >
            <Typography variant="h5" className={classes.title}>
              {title}
            </Typography>
            {titleRightComponent}
          </Box>
        ) : null}
        <div className={classes.wrapper}>
          {isLoading ? (
            <Box
              sx={{
                position: 'absolute',
                background: 'rgba(0,0,0,0.4)',
                left: 0,
                right: 0,
                top: '-25px',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignContent: 'center',
                borderRadius: '10px',
              }}
            >
              <LoadingSpinner color="success" />
            </Box>
          ) : null}
          {/* <LoadingSpinner /> */}
          <div
            className={classes.table__wrapper}
            style={{
              marginTop: title ? '40px' : '0px',
              maxHeight: maxHeight ? maxHeight : undefined,
              ['--sticky-top' as any]: `${stickyOffsetTop ?? (title ? 40 : 0)}px`,
            }}
          >
            <table
              className={
                stickyHeader
                  ? classes.table_sticky_full
                  : headers && headers?.length > 4
                    ? classes.largeTable
                    : classes.table
              }
            >
              {headers ? (
                <thead>
                  <tr>
                    {headers.map((item, index) => (
                      <th key={'tblHeader' + index} style={{ textAlign: item.textAlign || 'left' }}>
                        {item.label ? t(item.label) : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {items ? (
                  items.map((item, index) => renderTableRow(item, index))
                ) : !isLoading ? (
                  <tr>
                    <td colSpan={headers?.length}>
                      <Alert severity="warning">{noItemFoundMsg || 'No items found.'}</Alert>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && !hidePagination ? (
            <div className={classes.table__pagination}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  color="secondary"
                  variant="outlined"
                  shape="rounded"
                  onChange={handlePageChange}
                />
              </Stack>
            </div>
          ) : null}
        </div>
      </Card>
    </>
  );
};

export default UITable;
